import React, { useState, useRef, useEffect } from "react";
import {
  Grid,
  Menu,
  Dialog,
  MenuItem,
  IconButton,
  CircularProgress,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Calendar from "../../../components/calendar/Calender";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";

const PortOutsDashboard = () => {
  const [simData, setSimData] = useState(null);
  const agGridRef = useRef<AgGridReact | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(
    dayjs().add(-30, "days")
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(dayjs());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (selectedStartDate && selectedEndDate) {
      const startDateParam = selectedStartDate.format("YYYY-MM-DD");
      const endDateParam = selectedEndDate.format("YYYY-MM-DD");
      setSimData(null);
      GetSimData(startDateParam || "", endDateParam || "");
    }
  }, [selectedStartDate, selectedEndDate]);

  const exportGridDataAsCSV = () => {
    if (agGridRef.current) {
      agGridRef.current.api.exportDataAsCsv();
    }
  };

  const columnDefs: ColDef[] = [
    {
      field: "CustomerID",
      headerName: "CustomerID",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "MSISDN",
      headerName: "Msisdn",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Email",
      headerName: "Email",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "PortOutChannel",
      headerName: "PortOut Channel",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Network-PortedOut",
      headerName: "Network-PortedOut",
      filter: true,
      sortable: true,
      resizable: true,
    },

    {
      field: "PortedOutDate",
      headerName: "PortedOutDate",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "customer_age",
      headerName: "CustomerAge",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "LTV",
      headerName: "LTV",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "LastPlanSubscribed",
      headerName: "LastPlanSubscribed",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "FistPlanSubscribed",
      headerName: "FistPlanSubscribed",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "TotalRenewals",
      headerName: "TotalRenewals",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "CustomerType",
      headerName: "CustomerType",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "ReasonDescriptions",
      headerName: "ReasonDescriptions",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "AdditionalNotes",
      headerName: "AdditionalNotes",
      filter: true,
      sortable: true,
      resizable: true,
    },
  ];

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleCalendar = () => {
    setCalendarOpen(!calendarOpen);
    handleMenuClose();
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    if (date !== null && date.isValid()) {
      setSelectedStartDate(date);
    }
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    if (date !== null && date.isValid()) {
      setSelectedEndDate(date);
    }
  };

  const GetSimData = async (startDate: string, endDate: string) => {
    await axiosInterceptorInstance
      .get(
        `/dashboard/PortoutsViaDashboard?StartDate=${startDate}&EndDate=${endDate}`
      )
      .then((response) => {
        const responseData = JSON.parse(response.data.body);
        if (responseData.Data.length == 0) {
          toast.error("No Record Found");
        } else {
          setSimData(responseData.Data);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };
  const onRowClicked = (event: { data: any }) => {
    const rowData = event.data;
    setSelectedRowData(rowData);
    setOpen(true);
  };

  const TableRow = ({ label, value, isBackgroundGray = false }: any) => (
    <Grid container>
      <Grid item xs={3}>
        <p
          className="tableHeader"
          style={isBackgroundGray ? { backgroundColor: "#f2eded" } : {}}
        >
          {label}
        </p>
      </Grid>
      <Grid item xs={9}>
        <p
          className="tableData"
          style={isBackgroundGray ? { backgroundColor: "#f2eded" } : {}}
        >
          {value}
        </p>
      </Grid>
    </Grid>
  );

  const fields = [
    {
      label: "Customer ID",
      value: selectedRowData?.CustomerID,
      isBackgroundGray: true,
    },
    { label: "Msisdn", value: selectedRowData?.MSISDN },
    { label: "Email", value: selectedRowData?.Email, isBackgroundGray: true },

    {
      label: "PortOut Channel",
      value: selectedRowData?.["PortOutChannel"],
    },
    {
      label: "Network-PortedOut",
      value: selectedRowData?.["Network-PortedOut"],
      isBackgroundGray: true,
    },

    { label: "PortedOut Date", value: selectedRowData?.PortedOutDate },
    {
      label: "Customer Age",
      value: selectedRowData?.customer_age,
      isBackgroundGray: true,
    },
    { label: "LTV", value: selectedRowData?.LTV },
    {
      label: "Last Plan Subscribed",
      value: selectedRowData?.LastPlanSubscribed,
      isBackgroundGray: true,
    },
    {
      label: "First Plan Subscribed",
      value: selectedRowData?.FistPlanSubscribed,
    },
    {
      label: "Total Renewals",
      value: selectedRowData?.TotalRenewals,
      isBackgroundGray: true,
    },
    { label: "Customer Type", value: selectedRowData?.CustomerType },

    {
      label: "Reason",
      value: selectedRowData?.ReasonDescriptions,
      isBackgroundGray: true,
    },
    {
      label: "Additional Notes",
      value: selectedRowData?.AdditionalNotes,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div>
        <h2 className="heading">Web Dashboard: Port-Out Summary</h2>
        {selectedEndDate &&
          selectedStartDate &&
          Math.floor(selectedEndDate.diff(selectedStartDate, "day", true)) ===
            30 && <span className="small-text">Last 30 Days</span>}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          <button
            className="small-btn"
            style={{ visibility: simData ? "visible" : "hidden" }}
            onClick={() => {
              if (simData) {
                exportGridDataAsCSV();
              }
            }}
          >
            Export as CSV
          </button>
        </div>
        <IconButton
          onClick={(e: any) => {
            handleMenuClick(e);
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={toggleCalendar}></MenuItem>
          {selectedStartDate && selectedEndDate && (
            <Calendar
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
            />
          )}
        </Menu>
      </div>

      {!simData && (
        <div className="dummy-conatiner">
          <CircularProgress size={40} />
        </div>
      )}
      {simData && (
        <div
          className="ag-theme-alpine"
          style={{ width: "100%", height: "425px", overflowY: "auto" }}
        >
          <AgGridReact
            ref={agGridRef}
            paginationPageSize={25}
            pagination={true}
            rowData={simData}
            columnDefs={columnDefs}
            onRowClicked={onRowClicked}
          ></AgGridReact>
        </div>
      )}
      {selectedRowData != null && (
        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="dialogBox"
        >
          <h2
            style={{
              fontWeight: "bold",
              margin: "10px 0",
              paddingLeft: "10px",
            }}
          >
            Details
          </h2>
          <Grid
            container
            spacing={2}
            style={{
              margin: "3px 0 20px 0",
              width: "100%",
              padding: "0px 10px 0px 10px",
            }}
          >
            <Grid item xs={12} style={{ padding: "0" }}>
              {fields.map((field, index) => (
                <TableRow
                  key={index}
                  label={field.label}
                  value={field.value}
                  isBackgroundGray={field.isBackgroundGray}
                />
              ))}
            </Grid>
          </Grid>
        </Dialog>
      )}
    </motion.div>
  );
};

export default PortOutsDashboard;
